"use client";
import { ArrowUpRight } from "lucide-react";
import { useState, useEffect, useRef, useCallback, type CSSProperties } from "react";
import { motion, useMotionValue, useSpring } from "motion/react";
import Link from "next/link";
import { AccentStar } from "@/shared/ui/accent-star";
import type { PanelId, SectionMeta } from "@/entities/section";
import { TileFlair } from "./TileFlair";
import { SECTION_HOVER_RGBA, TILE_TRANSITION } from "../lib/constants";

export function SectionTile({
  panelId,
  meta,
  compact,
  hovered,
  siblingHovered,
  onHover,
  onLeave,
  onExpand,
  radius,
  contentHidden,
  protectedMode,
  isAnimating,
  activeTouchId,
  onPillClick,
  isExpanded,
  href,
  onLinkClick,
  isCollapsedSibling,
  isTouch,
}: {
  panelId: PanelId;
  meta: SectionMeta;
  compact: boolean;
  hovered: boolean;
  siblingHovered: boolean;
  onHover: () => void;
  onLeave: () => void;
  onExpand: () => void;
  radius: number;
  contentHidden: boolean;
  protectedMode: boolean;
  isAnimating: boolean;
  activeTouchId: string | null;
  onPillClick?: (e: React.MouseEvent) => void;
  isExpanded: boolean;
  href: string;
  onLinkClick: (e: React.MouseEvent) => void;
  isCollapsedSibling?: boolean;
  isTouch?: boolean;
}) {
  const m = meta;
  const isWhite = contentHidden || isExpanded;
  const tileRef = useRef<HTMLDivElement>(null);
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const springX = useSpring(mouseX, { stiffness: 320, damping: 32 });
  const springY = useSpring(mouseY, { stiffness: 320, damping: 32 });
  const [cursorVisible, setCursorVisible] = useState(false);
  const [inEmptyZone, setInEmptyZone] = useState(false);
  const isTouchActive = activeTouchId === panelId;
  // tileHeight tracked via ResizeObserver for height-based content visibility on mobile
  const [tileHeight, setTileHeight] = useState(999);
  const [hoveredPillIdx, setHoveredPillIdx] = useState<number | null>(null);

  useEffect(() => {
    if (!tileRef.current) return;
    const ro = new ResizeObserver((entries) => {
      setTileHeight(entries[0].contentRect.height);
    });
    ro.observe(tileRef.current);
    return () => ro.disconnect();
  }, []);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      // Skip synthetic mouse events on touch devices
      if (isTouch) return;
      const r = tileRef.current?.getBoundingClientRect();
      if (!r) return;
      const x = e.clientX - r.left;
      mouseX.set(x);
      mouseY.set(e.clientY - r.top);
      // Expanded: pill only appears in right 22% (beyond text+pills area ≈78%)
      setInEmptyZone(x > r.width * (isExpanded ? 0.78 : 0.666));
    },
    [mouseX, mouseY, isExpanded, isTouch],
  );

  const handleMouseEnter = useCallback(
    (e: React.MouseEvent) => {
      onHover();
      if (isTouch) return; // avoid synthetic mouseenter from touch
      const r = tileRef.current?.getBoundingClientRect();
      if (r) {
        const x = e.clientX - r.left,
          y = e.clientY - r.top;
        mouseX.set(x);
        mouseY.set(y);
        springX.set(x);
        springY.set(y);
        setInEmptyZone(x > r.width * (isExpanded ? 0.78 : 0.666));
      }
      if (!compact) setCursorVisible(true);
    },
    [onHover, compact, mouseX, mouseY, springX, springY, isExpanded, isTouch],
  );

  const handleMouseLeave = useCallback(() => {
    onLeave();
    setCursorVisible(false);
    setInEmptyZone(false);
  }, [onLeave]);

  // labelBottom = var(--py) — симметрично отступу сверху у заголовка.
  // Transition с лейбл-дива убран, поэтому резиновости нет.
  const labelBottom = compact ? "calc((var(--bar-h) - 17px) / 2)" : "var(--py)";
  const labelLeft = compact ? "calc(var(--tile-w) / 2)" : "var(--px)";

  const hoverBgColor = compact
    ? SECTION_HOVER_RGBA[panelId] || m.color
    : m.color;
  const bgTr = compact ? "opacity 0s" : "opacity 0.25s ease";

  return (
    <div
      ref={tileRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseMove={handleMouseMove}
      onClick={(e) => {
        if (protectedMode && !compact) {
          if (onExpand) onExpand();
        } else if (!compact) {
          // Тач и десктоп — одинаковая логика:
          // клик/тап на тайл разворачивает, навигация — через пилюлю/кнопку
          e.stopPropagation();
          if (!isExpanded) {
            if (onPillClick) onPillClick(e);
          }
        }
        // compact → navigation through <Link> overlay (см. ниже)
      }}
      className="relative w-full h-full overflow-hidden [container-type:size]"
      style={
        {
          borderRadius: radius,
          border: `1px solid ${m.color}`,
          transition: `${TILE_TRANSITION}, border-radius 0.35s ease`,
          cursor: compact ? "pointer" : "default",
          "--px": "clamp(14px, 8cqw, 36px)",
          // min 16px чтобы отступы не прижимали контент к краям даже на коротких строчках
          "--py": "clamp(16px, 11cqh, 44px)",
        } as React.CSSProperties
      }
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          zIndex: 0,
          borderRadius: "inherit",
          opacity: isWhite ? 0 : 1,
          backgroundColor: "rgba(255,255,255,0.55)",
          backdropFilter: isAnimating ? "none" : "blur(24px)",
          WebkitBackdropFilter: isAnimating ? "none" : "blur(24px)",
          boxShadow:
            "inset 0 0 0 1px rgba(255,255,255,0.4), 0 2px 16px rgba(1,33,104,0.04)",
          transition: `${bgTr}, backdrop-filter 0.1s ease`,
        }}
      />
      <div
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          zIndex: 0,
          borderRadius: "inherit",
          opacity: isWhite ? 1 : 0,
          backgroundColor: hoverBgColor,
          backdropFilter: !compact || isAnimating ? "none" : "blur(24px)",
          WebkitBackdropFilter: !compact || isAnimating ? "none" : "blur(24px)",
          transition: `${bgTr}, backdrop-filter 0.1s ease`,
        }}
      />

      {compact && (
        <Link
          href={href}
          prefetch
          aria-label={m.label}
          onClick={onLinkClick}
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 5,
            pointerEvents: "auto",
            borderRadius: "inherit",
          }}
        />
      )}

      {/* Десктопная пилюля (следует за курсором) */}
      {!compact && !isAnimating && (
        <motion.div
          className="hidden md:block"
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            x: springX,
            y: springY,
            translateX: "-50%",
            translateY: "-50%",
            zIndex: 30,
            pointerEvents:
              cursorVisible && hovered && (!isExpanded || inEmptyZone)
                ? "auto"
                : "none",
            cursor: "pointer",
            opacity:
              cursorVisible && hovered && (!isExpanded || inEmptyZone) ? 1 : 0,
            transition: "opacity 0.15s ease",
          }}
          onClick={(e) => {
            // Развёрнутый → Link внутри сам обработает навигацию.
            if (isExpanded) return;
            e.stopPropagation();
            if (onPillClick) onPillClick(e);
          }}
        >
          {isExpanded ? (
            <Link
              href={href}
              prefetch
              onClick={onLinkClick}
              style={{ display: "block", textDecoration: "none" }}
            >
              <div
                style={{
                  height: 44,
                  borderRadius: 22,
                  backgroundColor: "#ffffff",
                  border: `1.5px solid ${m.color}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "0 24px",
                  whiteSpace: "nowrap",
                }}
              >
                <span
                  className="select-none"
                  style={{
                    fontFamily: "var(--font-content)",
                    fontWeight: 600,
                    fontSize: "var(--fluid-text-sm)",
                    letterSpacing: "0.3px",
                    color: m.color,
                  }}
                >
                  Перейти в раздел
                </span>
              </div>
            </Link>
          ) : (
            <div
              style={{
                height: 44,
                borderRadius: 22,
                backgroundColor: "#ffffff",
                border: `1.5px solid ${m.color}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "0 24px",
                whiteSpace: "nowrap",
              }}
            >
              <span
                className="select-none"
                style={{
                  fontFamily: "var(--font-content)",
                  fontWeight: 600,
                  fontSize: "var(--fluid-text-sm)",
                  letterSpacing: "0.3px",
                  color: m.color,
                }}
              >
                Развернуть
              </span>
            </div>
          )}
        </motion.div>
      )}

      {/* Мобильная кнопка "В раздел" */}
      {!compact && !isAnimating && !contentHidden && isExpanded && (
        <Link
          href={href}
          prefetch
          onClick={onLinkClick}
          className="md:hidden"
          style={{
            position: "absolute",
            bottom: "var(--py)",
            right: "var(--px)",
            zIndex: 30,
            pointerEvents: "auto",
            cursor: "pointer",
            textDecoration: "none",
          }}
        >
          <div
            style={{
              height: 30,
              borderRadius: 15,
              backgroundColor: "#ffffff",
              border: `1.5px solid ${m.color}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "0 14px",
              whiteSpace: "nowrap",
            }}
          >
            <span
              className="select-none"
              style={{
                fontFamily: "var(--font-content)",
                fontWeight: 600,
                fontSize: "11px",
                letterSpacing: "0.4px",
                color: m.color,
              }}
            >
              В раздел
            </span>
          </div>
        </Link>
      )}
      {/* Иконка ↗ в правом верхнем углу развёрнутого тайла */}
      {!compact && !isAnimating && !contentHidden && isExpanded && (
        <div
          className="hidden md:block"
          style={{
            position: "absolute",
            top: "var(--py)",
            right: "var(--px)",
            zIndex: 5,
            pointerEvents: "none",
            color: "#ffffff",
          }}
        >
          <ArrowUpRight size={36} strokeWidth={2} />
        </div>
      )}
      {!compact && !contentHidden && !isAnimating && !isCollapsedSibling && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: isExpanded ? 0 : 1 }}
          transition={{ duration: 0.3 }}
          style={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            zIndex: 1,
          }}
        >
          <TileFlair panelId={panelId} color={m.color} />
        </motion.div>
      )}
      {!compact && !isAnimating && !isCollapsedSibling && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{
            opacity: !contentHidden && !isExpanded ? 1 : 0,
          }}
          transition={{ duration: 0.25 }}
          className="flex flex-col"
          style={{
            position: "absolute",
            inset: 0,
            // Extra bottom padding so text never reaches the label zone
            paddingTop: "var(--py)",
            paddingLeft: "var(--px)",
            paddingRight: "var(--px)",
            paddingBottom: "calc(var(--py) + 28px)",
            pointerEvents: "none",
            zIndex: 2,
            overflow: "hidden",
          }}
        >
          {/* Subtitle: скрываем на мобилке если тайл очень маленький */}
          {tileHeight >= 70 && (
            <div
              className="select-none"
              style={{
                width: "100%",
                maxWidth: "min(70cqw, max(200px, calc(100cqw - 160px)))",
                fontFamily: "var(--font-content)",
                color: "var(--foreground)",
                whiteSpace: "pre-line",
              }}
            >
              <div
                className="text-[clamp(16px,5cqw,24px)] md:text-[clamp(17px,5cqw,26px)]"
                style={{
                  fontWeight: 700,
                  marginBottom: "clamp(4px, 1.5cqw, 8px)",
                }}
              >
                {m.subtitle}
              </div>
              {/* Desktop: full hover description */}
              <div
                className="hidden md:block"
                style={{
                  fontWeight: 400,
                  fontSize: "clamp(13px, 3.5cqw, 17px)",
                  lineHeight: 1.35,
                  letterSpacing: "0.25px",
                  marginTop: "clamp(4px, 1.5cqh, 8px)",
                }}
              >
                {m.hoverDescription}
              </div>
            </div>
          )}
          {/* Mobile description only when tile is tall enough to avoid overlap */}
          {tileHeight >= 120 && (
            <div
              className="md:hidden select-none"
              style={{
                fontFamily: "var(--font-content)",
                fontWeight: 400,
                fontSize: "clamp(11px, 3cqw, 13px)",
                lineHeight: 1.3,
                letterSpacing: "0.25px",
                color: "var(--muted)",
                marginTop: "clamp(3px, 1cqh, 5px)",
              }}
            >
              {m.hoverDescription.split('\n')[0]}
            </div>
          )}
        </motion.div>
      )}
      {!compact && !isAnimating && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: isExpanded ? 1 : 0 }}
          transition={{ duration: 0.25 }}
          className="flex flex-col justify-between"
          style={{
            position: "absolute",
            inset: 0,
            paddingTop: "var(--py)",
            paddingLeft: "var(--px)",
            paddingRight: "var(--px)",
            paddingBottom: 0,
            pointerEvents: isExpanded ? "auto" : "none",
            zIndex: 2,
          }}
        >
          <div
            className="flex items-stretch justify-between w-full"
            style={{
              flexGrow: 1,
              paddingBottom: "calc(var(--py) + clamp(40px, 10cqw, 60px))",
            }}
          >
            <div
              className="flex flex-col"
              style={{ width: "100%", flexShrink: 0, height: "100%" }}
            >
              <div
                className="select-none"
                style={{
                  width: "100%",
                  maxWidth: "min(70cqw, max(200px, calc(100cqw - 160px)))",
                  fontFamily: "var(--font-content)",
                  color: "#fff",
                  whiteSpace: "pre-line",
                }}
              >
                <div
                  className="text-[clamp(16px,5cqw,24px)] md:text-[clamp(17px,5cqw,26px)]"
                  style={{
                    fontWeight: 700,
                    marginBottom: "clamp(4px, 1.5cqw, 8px)",
                  }}
                >
                  {m.subtitle}
                </div>
                {/* Desktop: always full description */}
                <div
                  className="hidden md:block"
                  style={{
                    fontWeight: 400,
                    lineHeight: 1.35,
                    letterSpacing: "0.25px",
                    fontSize: "clamp(13px, 3.5cqw, 17px)",
                  }}
                >
                  {m.description}
                </div>
                {/* Mobile: show full, short, or nothing based on tile height */}
                {tileHeight >= 150 && (
                  <div
                    className="md:hidden"
                    style={{
                      fontWeight: 400,
                      fontSize: "clamp(12px, 3.5cqw, 16px)",
                      lineHeight: 1.35,
                      letterSpacing: "0.25px",
                    }}
                  >
                    {m.description}
                  </div>
                )}
                {tileHeight >= 100 && tileHeight < 150 && (
                  <div
                    className="md:hidden"
                    style={{
                      fontWeight: 400,
                      fontSize: "clamp(12px, 3.5cqw, 16px)",
                      lineHeight: 1.35,
                      letterSpacing: "0.25px",
                    }}
                  >
                    {m.shortDescription || m.description}
                  </div>
                )}
              </div>
              <div style={{ flexGrow: 1, minHeight: 10 }} />
              <div
                className="flex flex-wrap gap-[clamp(6px,2cqw,10px)] justify-start"
                style={{
                  width: "100%",
                  maxWidth: "80cqw",
                }}
              >
                {m.pills.map((pill, i) => {
                  const pillHref = m.pillHrefs?.[i];
                  const pillDisabled = panelId === "media" || !pillHref;
                  const pillHovered = !pillDisabled && hoveredPillIdx === i;
                  const pillStyle: React.CSSProperties = {
                    height: "clamp(24px, 6cqw, 30px)",
                    borderRadius: 15,
                    border: `1px solid ${pillHovered ? m.color : "#fff"}`,
                    display: "flex",
                    alignItems: "center",
                    padding: "0 clamp(12px, 3cqw, 20px)",
                    backgroundColor: pillHovered ? "#fff" : "transparent",
                    color: pillHovered ? m.color : "#fff",
                    transition: "background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease",
                    cursor: pillDisabled ? "default" : "pointer",
                    opacity: (panelId === "media") ? 0.4 : 1,
                    pointerEvents: pillDisabled ? "none" : "auto",
                    textDecoration: "none",
                  };
                  const pillContent = (
                    <span
                      className="select-none whitespace-nowrap"
                      style={{
                        fontFamily: "var(--font-content)",
                        fontWeight: 400,
                        fontSize: "clamp(11px, 2.5cqw, 13px)",
                        lineHeight: "15px",
                        letterSpacing: "0.25px",
                        color: "inherit",
                      }}
                    >
                      {pill}
                    </span>
                  );
                  return pillHref ? (
                    <Link
                      key={`${pill}-${i}`}
                      href={pillHref}
                      scroll={false}
                      onClick={(e) => e.stopPropagation()}
                      onMouseEnter={() => setHoveredPillIdx(i)}
                      onMouseLeave={() => setHoveredPillIdx(null)}
                      style={pillStyle}
                    >
                      {pillContent}
                    </Link>
                  ) : (
                    <div
                      key={`${pill}-${i}`}
                      onMouseEnter={() => !pillDisabled && setHoveredPillIdx(i)}
                      onMouseLeave={() => setHoveredPillIdx(null)}
                      style={pillStyle}
                    >
                      {pillContent}
                    </div>
                  );
                })}
              </div>
              <div style={{ flexGrow: 2 }} />
            </div>
          </div>
        </motion.div>
      )}
      <div
        className="flex items-center gap-[6px]"
        style={
          isCollapsedSibling
            ? {
                // Вертикальный центр, левый флаг — var(--px) от кр
                position: "absolute",
                top: "50%",
                left: 0,
                transform: `translate(var(--px), -50%)`,
                zIndex: 10,
                pointerEvents: "none",
              }
            : {
                position: "absolute",
                bottom: 0,
                left: 0,
                transform: `translate(${labelLeft}, calc(-1 * ${labelBottom})) ${compact ? "translateX(-50%)" : ""}`,
                zIndex: 10,
                pointerEvents: "none",
              }
        }
      >
        <AccentStar color={isWhite ? "#fff" : m.color} size={10} />
        <span
          className="select-none whitespace-nowrap"
          style={{
            fontFamily: "var(--font-content)",
            fontWeight: 700,
            fontSize: "var(--fluid-text-xs)",
            lineHeight: "15px",
            letterSpacing: "0.25px",
            color: isWhite ? "#fff" : "var(--foreground)",
            transition: "color 0.25s ease",
          }}
        >
          {m.label}
        </span>
      </div>
    </div>
  );
}
